/**
 * AdminSetup - First-time setup screen for admin panel
 * Forces creation of secure admin credentials on fresh deployments
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Eye, EyeOff, Lock, Mail, AlertCircle, Shield, 
  CheckCircle, AlertTriangle, Key, ArrowRight
} from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSetup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);

  // Password strength criteria
  const passwordCriteria = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  const passwordStrength = Object.values(passwordCriteria).filter(Boolean).length;
  const isPasswordStrong = passwordStrength >= 4;
  const passwordsMatch = password === confirmPassword && password.length > 0;

  // Check if setup is required
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/setup-status`);
        if (response.data.setup_required) {
          setSetupRequired(true);
        } else {
          // Credentials already set, redirect to login
          navigate("/admin/login");
        }
      } catch (err) {
        console.error("Error checking setup status:", err);
        // If endpoint doesn't exist, assume setup not required
        navigate("/admin/login");
      } finally {
        setChecking(false);
      }
    };
    checkSetupStatus();
  }, [navigate]);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");

    // Validate
    if (!isPasswordStrong) {
      setError("Password must meet all security requirements");
      return;
    }
    
    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/initial-setup`, {
        email,
        password
      });

      if (response.data.success) {
        // Auto-login after setup
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("auth_email", email);
        localStorage.setItem("auth_expires", response.data.expires_at);
        
        // Navigate to admin with success message
        navigate("/admin", { state: { setupComplete: true } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!setupRequired) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-setup-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Security Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 bg-orange-500/10 border border-orange-500/30 mb-6"
        >
          <Shield className="w-6 h-6 text-orange-500 flex-shrink-0" />
          <div>
            <h3 className="text-orange-500 font-bold text-sm">First-Time Setup Required</h3>
            <p className="text-orange-400/80 text-xs mt-1">
              This admin panel has no credentials configured. Create a secure admin account to continue.
            </p>
          </div>
        </motion.div>

        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_363f79c5-085c-4461-8ddb-f89341e71c50/artifacts/832gaaj4_ASN%20Short%20Wordmark.png" 
            alt="Antler Sports Network"
            className="h-16 w-auto mx-auto mb-4"
            style={{ background: 'transparent' }}
          />
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
            Admin Setup
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            Create your secure admin credentials
          </p>
        </div>

        {/* Setup Form */}
        <form onSubmit={handleSetup} className="bg-zinc-900/50 border border-white/10 p-8">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 mb-6"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-500 text-sm">{error}</span>
            </motion.div>
          )}

          {/* Email Field */}
          <div className="mb-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Admin Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@yoursite.com"
                required
                data-testid="setup-email"
                className="w-full bg-zinc-800 border border-white/10 text-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-white/30 placeholder-zinc-600"
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="mb-4">
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a strong password"
                required
                data-testid="setup-password"
                className="w-full bg-zinc-800 border border-white/10 text-white pl-11 pr-12 py-3 text-sm focus:outline-none focus:border-white/30 placeholder-zinc-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          <div className="mb-6">
            <div className="flex gap-1 mb-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div 
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= passwordStrength 
                      ? passwordStrength >= 4 ? 'bg-green-500' : passwordStrength >= 3 ? 'bg-yellow-500' : 'bg-red-500'
                      : 'bg-zinc-700'
                  }`}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                { key: 'length', label: '8+ characters' },
                { key: 'uppercase', label: 'Uppercase letter' },
                { key: 'lowercase', label: 'Lowercase letter' },
                { key: 'number', label: 'Number' },
                { key: 'special', label: 'Special character' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  {passwordCriteria[key] ? (
                    <CheckCircle className="w-3 h-3 text-green-500" />
                  ) : (
                    <div className="w-3 h-3 border border-zinc-600 rounded-full" />
                  )}
                  <span className={passwordCriteria[key] ? 'text-green-500' : 'text-zinc-500'}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="mb-8">
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                data-testid="setup-confirm-password"
                className={`w-full bg-zinc-800 border text-white pl-11 pr-4 py-3 text-sm focus:outline-none placeholder-zinc-600 ${
                  confirmPassword.length > 0 
                    ? passwordsMatch ? 'border-green-500/50' : 'border-red-500/50'
                    : 'border-white/10 focus:border-white/30'
                }`}
              />
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Passwords do not match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !isPasswordStrong || !passwordsMatch}
            data-testid="setup-submit"
            className="w-full bg-orange-500 text-white hover:bg-orange-600 font-secondary font-bold uppercase tracking-widest text-sm py-4 h-auto rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Setting up...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Complete Setup
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>

        {/* Security Note */}
        <div className="text-center mt-6 text-xs text-zinc-500">
          <Shield className="w-4 h-4 inline-block mr-1" />
          Your credentials are securely encrypted and stored
        </div>
      </motion.div>

      {/* Noise Overlay */}
      <div className="noise-overlay" aria-hidden="true" />
    </div>
  );
};

export default AdminSetup;
